import {
  Box,
  Button,
  Checkbox,
  Stack,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { FILTERED_API_SERVICE_TYPE_OPTIONS } from "./apiServiceWizardConstants";
import { K, apiLabel, getAdapterTypeLabel } from "./apiServiceI18n";

export default function CreateApiServiceStep1({
  values,
  onChange,
  errors,
  baseUrlPreview,
  customUrlSummary,
  onOpenCustomUrl,
  onBlurApiName,
  onBlurWorkflowName,
  conformanceDisabled,
  validatingField,
  workflowsLoading,
}) {
  const { t } = useTranslation();
  const set = (field) => (e) => onChange(field, e.target.value);
  const setChecked = (field) => (_, checked) => onChange(field, checked);

  const fieldEndAdornment = (field) =>
    validatingField === field ? (
      <InputAdornment position="end">
        <CircularProgress size={18} aria-label={t(K.API_SVC_VALIDATING)} />
      </InputAdornment>
    ) : null;

  return (
    <Stack spacing={2.5}>
      <Typography variant="subtitle1" fontWeight={700}>
        {`${t(K.API_SVC_WIZARD_BREADCRUMB_CREATE)} > ${t(K.API_SVC_WIZARD_BREADCRUMB_STEP1)}`}
      </Typography>

      <TextField
        required
        label={apiLabel(K.LBL_NAME)}
        value={values.apiName}
        onChange={set("apiName")}
        onBlur={onBlurApiName}
        error={Boolean(errors.apiName)}
        helperText={errors.apiName}
        disabled={validatingField === "apiName"}
        InputProps={{ endAdornment: fieldEndAdornment("apiName") }}
        fullWidth
        size="small"
      />

      <FormControl fullWidth required size="small" error={Boolean(errors.apiType)}>
        <InputLabel id="api-svc-type-label">{t(K.API_SVC_API_SERVICE_TYPE)}</InputLabel>
        <Select
          labelId="api-svc-type-label"
          label={t(K.API_SVC_API_SERVICE_TYPE)}
          value={values.apiType}
          onChange={set("apiType")}
        >
          {FILTERED_API_SERVICE_TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {getAdapterTypeLabel(o.value, t)}
            </MenuItem>
          ))}
        </Select>
        {errors.apiType && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
            {errors.apiType}
          </Typography>
        )}
      </FormControl>

      <TextField
        required
        label={t(K.API_SVC_DESCRIPTION)}
        value={values.description}
        onChange={set("description")}
        error={Boolean(errors.description)}
        helperText={errors.description}
        fullWidth
        multiline
        minRows={3}
        size="small"
      />

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth required size="small" error={Boolean(errors.workflowGroup)}>
            <InputLabel id="wf-group-label">{apiLabel(K.WORKFLOW_GROUP)}</InputLabel>
            <Select
              labelId="wf-group-label"
              label={apiLabel(K.WORKFLOW_GROUP)}
              value={values.workflowGroup || ""}
              onChange={set("workflowGroup")}
              disabled={workflowsLoading}
            >
              {values.workflowGroupOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  {t(K.API_SVC_NO_WF_GROUPS)}
                </MenuItem>
              ) : (
                values.workflowGroupOptions.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))
              )}
            </Select>
            {workflowsLoading ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                {t(K.API_SVC_LOADING_WF_GROUPS)}
              </Typography>
            ) : null}
            {errors.workflowGroup && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.workflowGroup}
              </Typography>
            )}
          </FormControl>
        </Box>
        <Box sx={{ flex: 1 }}>
          <TextField
            required
            label={t(K.API_SVC_WF_NAME)}
            value={values.workflowName}
            onChange={set("workflowName")}
            onBlur={onBlurWorkflowName}
            error={Boolean(errors.workflowName)}
            helperText={errors.workflowName}
            disabled={validatingField === "workflowName"}
            InputProps={{ endAdornment: fieldEndAdornment("workflowName") }}
            fullWidth
            size="small"
          />
        </Box>
      </Stack>

      <TextField
        required
        label={t(K.API_SVC_BASE_URL)}
        value={baseUrlPreview}
        fullWidth
        size="small"
        InputProps={{ readOnly: true }}
        helperText={t(K.API_SVC_BASE_URL_HELP)}
      />

      <Box
        sx={{
          display: "flex",
          width: "100%",
          gap: 1,
          flexWrap: "nowrap",
          alignItems: "flex-end",
        }}
      >
        <TextField
          label={t(K.API_SVC_CUSTOM_URL)}
          value={customUrlSummary}
          size="small"
          error={Boolean(errors.customUrl)}
          helperText={
            errors.customUrl ||
            (values.customUrl?.normalizedKey
              ? `${t(K.API_SVC_NORMALIZED_EXECUTE_KEY)}: ${values.customUrl.normalizedKey}`
              : t(K.API_SVC_CUSTOM_URL_STEP1_HELP))
          }
          disabled={validatingField === "customUrl"}
          InputProps={{
            readOnly: true,
            endAdornment: fieldEndAdornment("customUrl"),
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Button
          variant="contained"
          onClick={onOpenCustomUrl}
          sx={{
            flexShrink: 0,
            whiteSpace: "nowrap",
            height: 35,
            px: 2,
            alignSelf: "flex-start",
          }}
        >
          {t(K.API_SVC_CUSTOM_URL)}
        </Button>
      </Box>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          bgcolor: "grey.50",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <FormControlLabel
          sx={{ m: 0, ml: 0, pl: 0, alignItems: "center" }}
          control={
            <Checkbox checked={values.conformance} onChange={setChecked("conformance")} />
          }
          label={t(K.API_SVC_CONFORMANCE)}
        />

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            rowGap: 2,
            width: "100%",
            mt: 2,
            ml: 0,
            pl: 0,
            boxSizing: "border-box",
          }}
        >
          <FormControlLabel
            sx={{ m: 0, ml: 0, pl: 0, flexShrink: 0, alignItems: "center" }}
            control={
              <Checkbox
                checked={values.conformanceExecute}
                disabled={conformanceDisabled}
                onChange={setChecked("conformanceExecute")}
              />
            }
            label={t(K.API_SVC_EXECUTE_WITHIN)}
          />

          <TextField
            placeholder={t(K.API_SVC_EXECUTE_WITHIN_PLACEHOLDER)}
            value={values.executeWithin}
            type="number"
            onChange={set("executeWithin")}
            disabled={conformanceDisabled || !values.conformanceExecute}
            error={Boolean(errors.executeWithin)}
            size="small"
            sx={{ width: 120, flexShrink: 0, m: 0 }}
            inputProps={{ inputMode: "numeric" }}
          />

          <RadioGroup
            row
            value={values.executeUnit}
            onChange={set("executeUnit")}
            sx={{
              flexShrink: 0,
              gap: 1,
              ml: 0,
              mr: 0,
              alignItems: "center",
              "& .MuiFormControlLabel-root": { m: 0, mr: 1 },
            }}
          >
            <FormControlLabel
              value="ms"
              disabled={conformanceDisabled || !values.conformanceExecute}
              control={<Radio size="small" />}
              label={t(K.API_SVC_EXECUTE_WITHIN_MS)}
            />
            <FormControlLabel
              value="sec"
              disabled={conformanceDisabled || !values.conformanceExecute}
              control={<Radio size="small" />}
              label={t(K.API_SVC_EXECUTE_WITHIN_SEC)}
            />
            <FormControlLabel
              value="min"
              disabled={conformanceDisabled || !values.conformanceExecute}
              control={<Radio size="small" />}
              label={t(K.API_SVC_EXECUTE_WITHIN_MIN)}
            />
          </RadioGroup>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ flexShrink: 0, lineHeight: 1, px: 0.5 }}
          >
            {t(K.API_SVC_AND)}
          </Typography>

          <FormControlLabel
            sx={{ m: 0, flexShrink: 0 }}
            control={
              <Checkbox
                checked={values.hasNoErrors}
                disabled={conformanceDisabled}
                onChange={setChecked("hasNoErrors")}
              />
            }
            label={t(K.API_SVC_HAS_NO_ERRORS)}
          />
        </Box>

        {errors.executeWithin ? (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 1, ml: 0, pl: 0 }}>
            {errors.executeWithin}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}
